import { createSelector } from '@reduxjs/toolkit'
import _isEmpty from 'lodash/isEmpty'
import { IntlShape } from 'react-intl'
//import _isNil from 'lodash/isNil'

import { RootState } from 'app/store'
import { makeTypedFetchSelectors, makeFetchSelectors } from 'app/redux-fetch'
import {
  HOME_REDUCER_NAME,
  PAYMENT_REDUCER_NAME,
  SELECTED_SUBSCRIPTION_PLAN_REDUCER_NAME,
  ESSENTIAL_PLAN,
} from 'features/home/consts'
import { INVOICE_REDUCER_NAME, SETUP_INTENT_REDUCER_NAME } from './consts'
import type { StripeIntent } from './types'
import { formatPrice, formatRecurringPrice } from './utils'
import { subscriptionManagementMessages } from './SubscriptionManagement/messages'
import {
  subscriptionHomePriceSelector,
  userSubscriptionPlanSelector,
} from '../../selectors'

export const selectedSubscriptionPlanSelector = createSelector(
  (store: RootState) =>
    store[HOME_REDUCER_NAME][SELECTED_SUBSCRIPTION_PLAN_REDUCER_NAME],
  (selectedSubscriptionPlan) => selectedSubscriptionPlan
)

export const selectedSubscriptionPlanPriceSelector = (
  formatMessage: IntlShape['formatMessage']
) =>
  createSelector(
    (store: RootState) =>
      store[HOME_REDUCER_NAME][SELECTED_SUBSCRIPTION_PLAN_REDUCER_NAME],
    (selectedSubscriptionPlans) =>
      selectedSubscriptionPlans.price
        ? formatRecurringPrice(
            selectedSubscriptionPlans.price,
            selectedSubscriptionPlans.interval,
            formatMessage
          )
        : formatMessage(subscriptionManagementMessages.freePrice)
  )

export const totalToPayAmountSelector = (
  formatMessage: IntlShape['formatMessage']
) =>
  createSelector(
    subscriptionHomePriceSelector.data,
    upcomingInvoiceSelectors.data,
    (subscriptionPrice, invoiceData) => {
      if (subscriptionPrice) {
        return formatPrice(subscriptionPrice.unit_amount)
      }
      return formatMessage(subscriptionManagementMessages.freePrice)
    }
  )

export const userSubscriptionIsSelectedSelector = createSelector(
  selectedSubscriptionPlanSelector,
  userSubscriptionPlanSelector,
  (selectedPlan, userPlan) =>
    !_isEmpty(selectedPlan) && selectedPlan.id === userPlan.id
)

export const userEssentialPlanIsSelectedSelector = createSelector(
  userSubscriptionIsSelectedSelector,
  userSubscriptionPlanSelector,
  (userSubscriptionIsSelected, userPlan) => {
    return userSubscriptionIsSelected && userPlan.id === ESSENTIAL_PLAN.id
  }
)

export const isSubscriptionDowngradeSelector = createSelector(
  userSubscriptionPlanSelector,
  selectedSubscriptionPlanSelector,
  (userPlan, selectedPlan) => (userPlan?.price ?? 0) > selectedPlan.price
)

export const setupIntentSelector = makeTypedFetchSelectors<StripeIntent>(
  HOME_REDUCER_NAME,
  PAYMENT_REDUCER_NAME,
  SETUP_INTENT_REDUCER_NAME
)

export const upcomingInvoiceSelectors = makeFetchSelectors(
  HOME_REDUCER_NAME,
  PAYMENT_REDUCER_NAME,
  INVOICE_REDUCER_NAME
)
